import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { CombinedConversationState } from '@nimbus-library/features/shared/models/enum/conversation-status.enum';
import { ReportingServiceSessionsService } from '@nimbus-global/features/reportingLiveData/reporting-service-sessions.service';
import { IUserMembership } from '@nimbus-portal/features/user-header/models/user-header-state.model';
import { UserHeaderStateService } from '@nimbus-portal/features/user-header/services/user-header-state.service';
import { HeaderBarNavigationService } from '@nimbus-portal/common/services/header-bar-navigation.service';
import { HeaderBarPinnedServicesService } from '@nimbus-portal/common/services/header-bar-pinned-services.service';

interface IPinnedServiceView {
  teamId: string;
  name: string;
  queue$: Observable<number>;
}

@Component({
  standalone: true,
  selector: 'nimbus-header-bar-pinned-services',
  templateUrl: './header-bar-pinned-services.component.html',
  styleUrl: './header-bar-pinned-services.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, SharedUIModule, TranslateModule],
})
export class HeaderBarPinnedServicesComponent implements OnInit {
  public readonly ICONS = ICONS;
  public readonly TranslationKeys = TranslationKeys;

  public pinnedServices$: Observable<IPinnedServiceView[]>;
  public isOnAttendantConsole$: Observable<boolean>;

  public constructor(
    private readonly userHeaderStateService: UserHeaderStateService,
    private readonly pinnedServicesService: HeaderBarPinnedServicesService,
    private readonly reportingServiceSessionsService: ReportingServiceSessionsService,
    private readonly headerBarNavigationService: HeaderBarNavigationService,
  ) {}

  public ngOnInit(): void {
    this.isOnAttendantConsole$ = this.headerBarNavigationService.isOnAttendantConsole$;
    this.pinnedServices$ = combineLatest([this.userHeaderStateService.teams$, this.pinnedServicesService.pinnedTeamIds$]).pipe(
      // Preserve pin order and skip pins whose membership no longer exists.
      map(([teams, pinnedIds]) =>
        pinnedIds
          .map((id) => teams.find((team) => team.teamId === id))
          .filter((team): team is IUserMembership => !!team)
          .map((team) => this.toView(team)),
      ),
    );
  }

  public onPinnedClick(service: IPinnedServiceView, isClickable: boolean): void {
    if (!isClickable) {
      return;
    }
    this.headerBarNavigationService.openTeamInAttendantConsole(service.teamId);
  }

  public unpin(service: IPinnedServiceView, event: Event): void {
    // Keep the chip's navigation click from firing when unpinning.
    event.stopPropagation();
    this.pinnedServicesService.toggle(service.teamId);
  }

  private toView(team: IUserMembership): IPinnedServiceView {
    return {
      teamId: team.teamId,
      name: team.teamContactInfo?.serviceName || team.teamContactInfo?.name || '',
      queue$: this.reportingServiceSessionsService
        .getServiceSessionsByTeamId$(team.teamId, [CombinedConversationState.InQueue])
        .pipe(map((sessions) => sessions.length)),
    };
  }
}
